import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { formatDateRu, formatTimeRu } from "../../../utils/appointmentLabels";
import {
  competitionAssemblyLabel,
  competitionVenueLine,
} from "../../../utils/competitionUtils";
import s from "./clientApp.module.css";

function ApplyField({ label, hint, children }) {
  return (
    <label className={s.compApplyField}>
      <span className={s.compApplyFieldLabel}>{label}</span>
      {children}
      {hint ? <span className={s.compApplyFieldHint}>{hint}</span> : null}
    </label>
  );
}

export default function CompetitionApplySheet({
  competition,
  onClose,
  onSubmit,
  submitting,
}) {
  const [weight, setWeight] = useState("");
  const [age, setAge] = useState("");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      onSubmit({
        athlete_weight_kg: weight.trim() || undefined,
        athlete_age: age.trim() || undefined,
        notes: notes.trim() || undefined,
      });
    },
    [age, notes, onSubmit, weight],
  );

  if (!competition) return null;

  const venue = competitionVenueLine(competition);
  const assembly = competitionAssemblyLabel(competition);
  const title = competition.title || "Соревнование";

  return createPortal(
    <div className={s.sheetBackdrop} onClick={onClose} role="presentation">
      <div
        className={`${s.sheet} ${s.competitionDetailSheet} ${s.compApplySheet}`}
        role="dialog"
        aria-labelledby="comp-apply-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className={s.sheetHandle} aria-hidden />
        <button type="button" className={s.sheetClose} onClick={onClose} aria-label="Закрыть">
          <svg viewBox="0 0 24 24" aria-hidden>
            <path
              d="M6 6l12 12M18 6 6 18"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>

        <div className={s.compApplyHero}>
          <div className={s.compApplyHeroGlow} aria-hidden />
          <span className={s.compSheetTag}>Заявка</span>
          <h2 id="comp-apply-title" className={s.compApplyTitle}>
            {title}
          </h2>
          <div className={s.compApplyChips}>
            {competition.start_datetime ? (
              <span className={s.compApplyChip}>
                {formatDateRu(competition.start_datetime, {
                  weekday: "short",
                  day: "numeric",
                  month: "long",
                })}
                {" · "}
                {formatTimeRu(competition.start_datetime)}
              </span>
            ) : null}
            {venue ? <span className={s.compApplyChip}>{venue}</span> : null}
            {assembly ? <span className={s.compApplyChip}>{assembly}</span> : null}
          </div>
        </div>

        <form onSubmit={handleSubmit} className={s.compApplyForm}>
          <div className={s.compApplyBody}>
            <p className={s.compApplyLead}>
              Заполните данные спортсмена — тренер увидит заявку и свяжется при
              необходимости.
            </p>

            <div className={s.compApplyGrid}>
              <ApplyField label="Вес, кг" hint="До десятых, например 45.5">
                <input
                  className={s.compApplyInput}
                  type="number"
                  inputMode="decimal"
                  min="1"
                  max="300"
                  step="0.1"
                  value={weight}
                  onChange={(e) => setWeight(e.target.value)}
                  placeholder="45.5"
                />
              </ApplyField>

              <ApplyField label="Возраст, лет" hint="На день соревнования">
                <input
                  className={s.compApplyInput}
                  type="number"
                  inputMode="numeric"
                  min="3"
                  max="99"
                  value={age}
                  onChange={(e) => setAge(e.target.value)}
                  placeholder="12"
                />
              </ApplyField>
            </div>

            <ApplyField label="Комментарий" hint="Разряд, весовая категория, пожелания">
              <textarea
                className={`${s.compApplyInput} ${s.compApplyTextarea}`}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Например: 2 разряд, до 48 кг"
                rows={3}
              />
            </ApplyField>
          </div>

          <div className={s.compApplyFooter}>
            <button
              type="button"
              className={s.compApplyCancel}
              onClick={onClose}
              disabled={submitting}
            >
              Отмена
            </button>
            <button type="submit" className={s.compApplySubmit} disabled={submitting}>
              {submitting ? "Отправка…" : "Отправить заявку"}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
}
